/**
 * Formik-bound QueryBuilder wrapper for workflow rules.
 * Sibling of `CommunicationConditionsQueryBuilderFormik` — same skeleton
 * (read `values[fieldId]`, gate on `submitCount`, compute prereqs, call the
 * shared registry hook, fire `onFieldsLoaded`, render the pure QB) but
 * parameterised over the `workflow_rule` rule_set_type.
 *
 * Workflow rules dispatch via an `event_key`, just like comms — so the
 * catalog fetch passes the event through and the BE narrows the field
 * registry by event scope. Prereq gate matches the comms wrapper: an event
 * and a LOB scope (concrete LOBs or the all-LOBs flag) plus a sub-org scope
 * must be picked before the catalog is fetched.
 *
 * The composed pure `RuleConditionsQueryBuilder` writes its tree into Formik
 * under `fieldId` (default `'conditions_json'`).
 */

import React, { useEffect } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { getIn, useFormikContext } from 'formik';

import useOrganization from '../../../../OrganizationContext';
import { checkConditionPrerequisites } from '../../Conditions/QueryBuilder/prerequisites';
import RuleConditionsQueryBuilder from '../../Conditions/QueryBuilder/RuleConditionsQueryBuilder';
import type { FieldDefinitionDto } from '../../Conditions/QueryBuilder/types';
import { useRuleSetConditionFieldRegistry } from '../../Conditions/QueryBuilder/useRuleSetConditionFieldRegistry';

const WORKFLOW_RULE_TYPE = 'workflow_rule';

interface WorkflowConditionsQueryBuilderFormikProps {
  /** Formik path holding the `conditions_json` tree (default: `'conditions_json'`). */
  fieldId?: string;
  /** Event firing the rule. Empty string = "not yet picked" — the catalog
   *  fetch is gated until the user selects an event (the BE needs it to
   *  filter the catalog by event scope). */
  eventKey: string;
  /** Every LOB the rule is scoped to. Forwarded whole: the catalog is the
   *  cross-LOB intersection, and a rule offered one LOB's fields can be saved
   *  with conditions the other LOBs' claims never carry. */
  lobs: string[];
  /** True when the rule is scoped to every LOB, present and future. Satisfies
   *  the LOB prerequisite on its own and is forwarded as `is_all_lobs`. */
  isAllLobs: boolean;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
  /** Lets the parent form mirror the registry into its own state for use in
   *  Formik's form-level `validate` prop. */
  onFieldsLoaded?: (fields: FieldDefinitionDto[]) => void;
  /** Surfaces the catalog-fetch loading state so the parent dialog can disable
   *  Save while fields are still in flight. When prereqs aren't met the
   *  fetcher is idle (isLoading=false), so Save behaves normally outside of
   *  fetches. */
  onLoadingChange?: (isLoading: boolean) => void;
}

const WorkflowConditionsQueryBuilderFormik: React.FC<WorkflowConditionsQueryBuilderFormikProps> = ({
  fieldId = 'conditions_json',
  eventKey,
  lobs,
  isAllLobs,
  isAllSubOrgs,
  subOrgIds,
  onFieldsLoaded,
  onLoadingChange,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const value = (getIn(values, fieldId) as RuleGroupType | null | undefined) ?? null;

  // `useOrganization` is exported from a JSX file without types; cast at the boundary.
  const { organizationId, subOrganizationEnabled } = useOrganization() as {
    organizationId: number;
    subOrganizationEnabled?: boolean;
  };

  const { prerequisitesMet, prerequisiteMessage } = checkConditionPrerequisites(
    eventKey,
    isAllLobs,
    lobs,
    isAllSubOrgs,
    subOrgIds,
    !!subOrganizationEnabled
  );

  const { fields, isLoading } = useRuleSetConditionFieldRegistry({
    organizationId,
    enabled: prerequisitesMet,
    ruleSetType: WORKFLOW_RULE_TYPE,
    eventKey,
    lobs,
    isAllLobs,
    isAllSubOrgs,
    subOrgIds,
  });

  // Intentionally only depends on `fields` — `onFieldsLoaded` identity is
  // irrelevant. Including it would silently infinite-loop if a caller passes
  // a non-memoized inline arrow.
  useEffect(() => {
    onFieldsLoaded?.(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  // Same callback-identity caveat as onFieldsLoaded: depend on the boolean
  // value only, not the callback. Parent dialogs typically wire this to a
  // `setIsLoading` setState from useState, whose identity is stable anyway.
  useEffect(() => {
    onLoadingChange?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <RuleConditionsQueryBuilder
      value={value}
      onChange={(q) => setFieldValue(fieldId, q)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={prerequisiteMessage}
      emptyMessage="No condition fields available for this event and LOB."
      // A workflow rule dispatches on its `event_key`, so it always has an event time.
      hasTriggeringEvent
    />
  );
};

export default WorkflowConditionsQueryBuilderFormik;
